/**
 * JAVASCRIPT DEVELOPER DOCUMENTATION
 *
 * Symmetry is a powerful and customizable theme designed for large-scale e-commerce stores. Built
 * using Web Components, it offers a highly modular architecture that makes customization and
 * maintenance easier than ever. In addition, Symmetry is optimized for speed, ensuring that your
 * store runs as fast as possible to provide your customers with a seamless shopping experience.
 *
 * If you would like to add your own JS to Symmetry, we recommend using this file and referencing
 * it using Theme Settings > Advanced > Custom HTML.
 *
 * As a brief overview, Symmetry:
 *  - Broadcasts many JS events.
 *  - Is built using Web Components.
 *  - Leverages 'code splitting' for some features.
 *  - Is completely custom built (no JS libraries)
 *  - Has a number of JS utilities.
 *
 *
 *
 * =================================================================================================
 * Custom JavaScript Events
 * =================================================================================================
 *
 * Symmetry broadcasts many custom events for ease of extensibility, detailed in this section.
 *
 * When in the Theme Editor, the details of each custom event will be logged out in the Dev Tools
 * console everytime it is triggered.
 *
 * Events are named in the following convention: ["on/dispatch"]:[subject]:[action] (where
 * 'dispatch' will trigger an event to occur, and 'on' indicates an event has occurred).
 *
 * All 'Return data' detailed in this section is returned within the 'event.detail' object.
 *
 * The available events are:
 *  1.  on:variant:change
 *  2.  on:cart:add
 *  3.  on:cart:error
*   4.  on:cart:after-merge
 *  5.  on:cart-drawer:before-open
 *  6.  on:cart-drawer:after-open
 *  7.  on:cart-drawer:after-close
 *  8.  on:quickbuy:before-open
 *  9.  on:quickbuy:after-open
 *  10. on:quickbuy:after-close
 *  11. dispatch:cart-drawer:open
 *  12. dispatch:cart-drawer:refresh
 *  13. dispatch:cart-drawer:close
 *  14. on:debounced-resize
 *  15. on:breakpoint-change
 *
 * -------------------------------------------------------------------------------------------------
 * 1) on:variant:change
 * -------------------------------------------------------------------------------------------------
 * Fires whenever a variant is selected (e.g. Product page, Quickbuy, Featured Product etc).
 *
 * How to listen:
 * document.addEventListener('on:variant:change', (event) => {
 *  // your code here
 * });
 *
 * Returned data:
 *  - form: the product form content
 *  - variant: the selected variant object
 *  - allVariants: an array of all variants
 *  - selectedOptions: an array of currently selected options (e.g. ['Blue', 'Large'])
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) on:cart:add
 * -------------------------------------------------------------------------------------------------
 * Fires when a variant has been added to the cart, where it didn't exist in the cart before. This
 * event does not fire when the added variant was already in the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:add', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - variantId: id of the variant that was just added to the cart
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) on:cart:error
 * -------------------------------------------------------------------------------------------------
 * Fires when an action related to the cart has failed, for example adding too much quantity of an
 * item to the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:error', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - error: the error message
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 4) on:cart:after-merge
 * -------------------------------------------------------------------------------------------------
 * Fires after a list of cart items has finished being dynamically updated after a cart change.
 *
 * How to listen:
 * document.addEventListener('on:cart:after-merge', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 5) on:cart-drawer:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the cart drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 6) on:cart-drawer:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 7) on:cart-drawer:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 8) on:quickbuy:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the quick buy drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 9) on:quickbuy:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 10) on:quickbuy:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 11) dispatch:cart-drawer:open
 * -------------------------------------------------------------------------------------------------
 * Opens the cart drawer (if enabled in the Theme Settings).
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
 *
 * You can optionally pass in a 'detail' object with a property of 'opener', which specifies the DOM
 * element that should be focussed on when the drawer is closed.
 *
 * Example:
 * document.getElementById('header-search').addEventListener('keydown', (evt) => {
 *   if (evt.keyCode === 67) {
 *     evt.preventDefault();
 *     document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
 *       detail: {
 *         opener: evt.target
 *       }
 *     }));
 *   }
 * });
 *
 * In this example, we attach a keydown listener to the search input in the header. If the user
 * presses the 'c' key, it prevents the default behavior (which would be to type the letter 'c' in
 * the input) and dispatches the 'dispatch:cart-drawer:open' event with a 'detail' object that
 * specifies the search input as the opener. When the cart drawer is closed, focus is returned to
 * the search input.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 12) dispatch:cart-drawer:refresh
 * -------------------------------------------------------------------------------------------------
 * Refreshes the contents of the cart drawer.
 *
 * This event is useful when you are adding variants to the cart and would like to instruct the
 * theme to re-render the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
 *   bubbles: true
 * }));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 13) dispatch:cart-drawer:close
 * -------------------------------------------------------------------------------------------------
 * Closes the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 14) on:debounced-resize
 * -------------------------------------------------------------------------------------------------
 * Fires when the viewport finishes resizing (debounced to 300ms by default).
 *
 * How to listen:
 * window.addEventListener('on:debounced-resize', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 15) on:breakpoint-change
 * -------------------------------------------------------------------------------------------------
 * Fires when the breakpoint of the viewport changes.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  if (theme.mediaMatches.md) {
 *   console.log('we are not on mobile');
 *  }
 * });
 *
 *
 *
 * =================================================================================================
 * Web Components
 * =================================================================================================
 *
 * Symmetry utilizes Web Components to the fullest.
 *
 * Web Components are a set of standardized APIs that allow developers to create custom, reusable
 * HTML elements that can be used across different web pages and applications.
 * Web Components consist of three main technologies: Custom Elements, Shadow DOM and HTML
 * Templates.
 *
 * See Mozilla for more: https://developer.mozilla.org/en-US/docs/Web/Web_Components
 *
 *
 *
 =================================================================================================
 * Third-Party JavaScript Dependencies
 * =================================================================================================
 *
 * Symmetry has no third-party JavaScript dependencies.
 *
 *
 * =================================================================================================
 *
 * Have fun! - The Clean Canvas Development Team.
 */

var GiftWrapModule = GiftWrapModule || {};

GiftWrapModule.isProductInCart = function(searchTitle) {
  // Query all product title links inside the cart items
  const titleElements = document.querySelectorAll('.cart-item__title a');
  for (const el of titleElements) {
    if (el.textContent.trim().toLowerCase() === searchTitle.toLowerCase()) {
      return true;
    }
  }
  return false;
};

GiftWrapModule.initGiftMessageOption = function() {
  const giftWrapCheckbox = document.getElementById('gift-wrap');
  const giftMessage = document.getElementById('gift-message-option');

  if (giftWrapCheckbox && giftMessage) {
    giftWrapCheckbox.disabled = false;
    const giftProductTitle = giftMessage.getAttribute('data-gift-product-title');
    if (giftProductTitle) {
      giftMessage.style.display = giftWrapCheckbox.checked ? 'block' : 'none';
      if (GiftWrapModule.isProductInCart(giftProductTitle)) {
        giftMessage.style.display = 'none';
        giftWrapCheckbox.checked = true;
        giftWrapCheckbox.disabled = true;
      }
      function giftWrapChangeHandler() {
        if (!GiftWrapModule.isProductInCart(giftProductTitle)) {
          giftMessage.style.display = this.checked ? 'block' : 'none';
        } else {
          giftMessage.style.display = 'none';
        }
      }
      // Remove any existing listener if necessary to prevent duplicates.
      giftWrapCheckbox.removeEventListener('change', giftWrapChangeHandler);
      giftWrapCheckbox.addEventListener('change', giftWrapChangeHandler);
    }
  }
};

// Listen for custom events and DOM ready state
document.addEventListener('on:cart-drawer:after-open', function(event) {
  GiftWrapModule.initGiftMessageOption();
});

document.addEventListener('on:cart:after-merge', function(event) {
  setTimeout(() => {
    GiftWrapModule.initGiftMessageOption();
  }, 500);
});

document.addEventListener('DOMContentLoaded', function() {
  if (window.location.pathname === '/cart') {
    GiftWrapModule.initGiftMessageOption();
  }
});

const cleanOmnisendStyles = styleEl => {
  if (styleEl.textContent.includes('omnisend')) {
    let cleaned = styleEl.textContent;

    // 1. Remove !important
    cleaned = cleaned.replace(/\s*!important\b/g, '');

    // 2. Remove placeholder pseudo-selectors
    cleaned = cleaned.replace(/[^}]*::placeholder[^}]*}/gi, '');
    cleaned = cleaned.replace(/[^}]*::-webkit-input-placeholder[^}]*}/gi, '');
    cleaned = cleaned.replace(/[^}]*::-moz-placeholder[^}]*}/gi, '');
    cleaned = cleaned.replace(/[^}]*:-ms-input-placeholder[^}]*}/gi, '');

    // 3. Remove rules for text inputs with omnisend-form and -input
    cleaned = cleaned.replace(
      /[^}]*input(?:\[type=['"]text['"]\])?[^\s,{>]*[.#][^\s,{]*omnisend-form[^\s,{]*-input[^\s,{]*[^}]*}/gi,
      ''
    );

    // 4. Remove all header styles (h1–h6)
    cleaned = cleaned.replace(/[^}]*\bh[1-6]\b[^}]*}/gi, '');

    // 5. Remove all rules starting with or containing `.omnisend-reset-`
    cleaned = cleaned.replace(/[^}]*\.omnisend-reset-[^\s,{]*[^}]*}/gi, '');

    // 6. Remove all font-size declarations (across any rules)
    cleaned = cleaned.replace(/font-size\s*:\s*[^;{}]+[;]?/gi, '');

    styleEl.textContent = cleaned;
  }
};



// Check existing style tags (in case some are already present)
document.querySelectorAll('style').forEach(cleanOmnisendStyles);

// Set up observer for future style elements
const observer = new MutationObserver(mutations => {
  mutations.forEach(mutation => {
    mutation.addedNodes.forEach(node => {
      if (node.tagName === 'STYLE') {
        cleanOmnisendStyles(node);
      } else if (node.nodeType === 1) {
        // Check if this node contains <style> tags inside
        node.querySelectorAll?.('style').forEach(cleanOmnisendStyles);
      }
    });
  });
});

observer.observe(document.documentElement, {
  childList: true,
  subtree: true
});


      
